import React from 'react';
import clsx from 'clsx';
import type { ClientSummary } from '@/features/Clients/Data';
import {
  SITUACAO_ATIVO_LABELS,
  SITUACAO_ATIVO_PONTO,
  SITUACOES_ATIVO,
  TIPO_ATIVO_LABELS,
} from './ativos.definitions';
import type { FacetaAtivos, SelecaoAtivos } from './ativos.utils';
// Reaproveitados do painel de Solicitações — mesmo visual/comportamento nas telas do SI.
import ProjetoFiltroDropdown from '../../../SolicitacoesListPage/components/ProjetoFiltroDropdown';
import ResumoFacetaSecao from '../../../SolicitacoesListPage/components/ResumoFacetaSecao';

interface AtivosResumoPainelProps {
  contagens: Record<FacetaAtivos, Map<string, number>>;
  selecao: SelecaoAtivos;
  onChange: (parcial: Partial<SelecaoAtivos>) => void;
  clientes: ClientSummary[];
  mostrarTipo?: boolean;
}

function alternar<T>(lista: T[], valor: T): T[] {
  return lista.includes(valor) ? lista.filter((item) => item !== valor) : [...lista, valor];
}

/**
 * Painel de resumo da aba Ativos (mesmo formato dos painéis de Solicitações/Estadias):
 * placar de Situação clicável — só as situações que têm ativo (ou estão marcadas), são 10
 * no total —, Projeto no cabeçalho, e linhas de Tipo e Propriedade.
 */
export default function AtivosResumoPainel({ contagens, selecao, onChange, clientes, mostrarTipo = true }: AtivosResumoPainelProps) {
  const totalTodos = Array.from(contagens.situacoes.values()).reduce((soma, n) => soma + n, 0);
  const todosSelecionado = selecao.situacoes.length === 0;
  const algumFiltro = Object.values(selecao).some((lista) => lista.length > 0);
  // "Raro" + o nome de cada Cliente dono (Propriedade na ficha = Raro ou um Cliente).
  const nomePorClienteId = new Map(clientes.map((c) => [c.id, c.name]));
  const opcoesPropriedade = [
    'raro',
    ...Array.from(new Set([...Array.from(contagens.propriedades.keys()), ...selecao.propriedades]))
      .filter((chave) => chave !== 'raro')
      .sort((a, b) => (nomePorClienteId.get(a) ?? '').localeCompare(nomePorClienteId.get(b) ?? '', 'pt-BR')),
  ];
  const rotulosPropriedade = Object.fromEntries(
    opcoesPropriedade.map((chave) => [chave, chave === 'raro' ? 'Raro' : (nomePorClienteId.get(chave) ?? 'Cliente não encontrado')])
  );
  const situacoesVisiveis = SITUACOES_ATIVO.filter(
    (s) => (contagens.situacoes.get(s) ?? 0) > 0 || selecao.situacoes.includes(s)
  );

  const classeTile = (selecionado: boolean) =>
    clsx(
      'relative flex min-w-[8rem] flex-1 flex-col items-start gap-0.5 px-4 py-3 text-left transition',
      selecionado ? 'bg-blue-50' : 'bg-white hover:bg-gray-50'
    );
  const classeNumero = (selecionado: boolean) =>
    clsx('text-2xl font-semibold tabular-nums', selecionado ? 'text-blue-700' : 'text-gray-900');

  return (
    <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <header className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-4 py-3">
        <h2 className="mr-auto font-montserrat text-sm font-semibold text-gray-900">Resumo</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500">Projeto</span>
          <ProjetoFiltroDropdown
            clientes={clientes}
            contagem={contagens.projetos}
            selecionados={selecao.projetos}
            onChange={(projetos) => onChange({ projetos })}
          />
        </div>
        {algumFiltro && (
          <button
            type="button"
            onClick={() => onChange({ situacoes: [], tipos: [], propriedades: [], projetos: [] })}
            className="text-xs font-medium text-blue-600 hover:underline"
          >
            Limpar filtros
          </button>
        )}
      </header>

      <div className="flex flex-wrap gap-px border-b border-gray-100 bg-gray-100">
        <button type="button" aria-pressed={todosSelecionado} onClick={() => onChange({ situacoes: [] })} className={classeTile(todosSelecionado)}>
          <span className={classeNumero(todosSelecionado)}>{totalTodos}</span>
          <span className="text-xs font-medium text-gray-500">Todos</span>
          {todosSelecionado && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-blue-600" />}
        </button>
        {situacoesVisiveis.map((situacao) => {
          const selecionado = selecao.situacoes.includes(situacao);
          return (
            <button
              key={situacao}
              type="button"
              aria-pressed={selecionado}
              onClick={() => onChange({ situacoes: alternar(selecao.situacoes, situacao) })}
              className={classeTile(selecionado)}
            >
              <span className={classeNumero(selecionado)}>{contagens.situacoes.get(situacao) ?? 0}</span>
              <span className="flex items-center gap-1.5 text-xs font-medium text-gray-500">
                <span className={clsx('h-2 w-2 shrink-0 rounded-full', SITUACAO_ATIVO_PONTO[situacao])} />
                {SITUACAO_ATIVO_LABELS[situacao]}
              </span>
              {selecionado && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-blue-600" />}
            </button>
          );
        })}
      </div>

      <div className="space-y-2 px-4 py-3">
        {mostrarTipo && (
          <ResumoFacetaSecao
            titulo="Tipo"
            opcoes={Object.keys(TIPO_ATIVO_LABELS)}
            rotulos={TIPO_ATIVO_LABELS}
            contagem={contagens.tipos}
            selecionados={selecao.tipos}
            onChange={(tipos) => onChange({ tipos })}
          />
        )}
        <ResumoFacetaSecao
          titulo="Propriedade"
          opcoes={opcoesPropriedade}
          rotulos={rotulosPropriedade}
          contagem={contagens.propriedades}
          selecionados={selecao.propriedades}
          onChange={(propriedades) => onChange({ propriedades })}
        />
      </div>
    </section>
  );
}
